import { Box, Theme } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import Tooltip from 'components/Tooltip';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store/hooks';
import {
  selectConnectionQualityLevel,
  selectConnectionQualityV2Enabled,
} from 'features/system-stats/systemStatsSlice';
import { selectLocalVideoBroadcasting } from 'features/streaming/streamingSlice';
import { ConnectionQualityLevel } from 'utils/webrtc/ConnectionQuality';

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  borderRadius: theme.spacing(2),
  backgroundColor: alpha(theme.palette.common.white, 0.3),
  padding: theme.spacing(1),
  cursor: 'default',
}));

const barHeights = [6, 11, 16];

const activeBarsCount: Record<ConnectionQualityLevel, number> = {
  [ConnectionQualityLevel.good]: 3,
  [ConnectionQualityLevel.fair]: 2,
  [ConnectionQualityLevel.poor]: 1,
};

const getBarColor = (barIndex: number, level: ConnectionQualityLevel, theme: Theme): string => {
  const activeBars = activeBarsCount[level];

  if (barIndex >= activeBars) {
    return 'rgba(255, 255, 255, 0.3)';
  }

  if (level === ConnectionQualityLevel.good) {
    return theme.palette.success.light;
  }

  if (level === ConnectionQualityLevel.fair) {
    return theme.palette.warning.light;
  }

  return theme.palette.error.main;
};

const tooltipKeys: Record<ConnectionQualityLevel, string> = {
  [ConnectionQualityLevel.good]: 'connection_indicator.good',
  [ConnectionQualityLevel.fair]: 'connection_indicator.fair',
  [ConnectionQualityLevel.poor]: 'connection_indicator.poor',
};

const ConnectionIndicator = () => {
  const { t } = useTranslation('room');
  const qualityLevel = useAppSelector(selectConnectionQualityLevel);
  const videoBroadcasting = useAppSelector(selectLocalVideoBroadcasting);
  // Under V2 the orchestrator computes MOS from receive-side tracks too, so
  // attendees have a real quality signal. Legacy MOS is publisher-only, so
  // keep that gate when V2 is off — once legacy-publisher.ts is deleted the
  // v2Enabled clause can go.
  const v2Enabled = useAppSelector(selectConnectionQualityV2Enabled);

  if (qualityLevel === null) {
    return null;
  }

  if (!v2Enabled && !videoBroadcasting) {
    return null;
  }

  return (
    <Tooltip title={t(tooltipKeys[qualityLevel])} placement="bottom">
      <Root data-testid="connection-indicator">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: '3px',
            height: 24,
            width: 24,
            justifyContent: 'center',
            pb: 1,
          }}
        >
          {barHeights.map((height, index) => (
            <Box
              key={index}
              data-testid={`signal-bar-${index + 1}`}
              sx={(theme) => ({
                width: 3,
                height,
                backgroundColor: getBarColor(index, qualityLevel, theme),
                transition: theme.transitions.create('background-color'),
              })}
            />
          ))}
        </Box>
      </Root>
    </Tooltip>
  );
};

export default ConnectionIndicator;
